import { Validators } from '@angular/forms';
import { InsuredObjectFormConfig } from '@models/insured-object-form-config';
import {
    PortalProductConfig,
    OrderProductConfig,
    ProductPresentation,
    getProductWithVariants,
    ProductWithVariants,
    Product,
} from '@models/product';
import { MANUFACTUREUR_REQUIRED, MANUFACTUREUR_TOO_SHORT, PRICE_UP_TO_1500 } from '@config/error-messages.const';
import { CommonValidators } from 'app/shared/validators/common-validators';
import {
    billingAddress,
    defaultLandingPage,
    insuredObjects,
    paymentData,
    submitContract,
} from '@config/order-step-configs';
import { defaultContractWidgets } from '@config/default-contract-widgets';
import { TabletPlusInsuranceWidgetComponent } from '../widgets/tablet-plus-insurance-widget/tablet-plus-insurance-widget.component';
import { OrderState } from 'app/order/models';
import { getTagsFromProduct } from 'app/products/fns';
import { SettlementPeriodSwitchComponent } from 'app/order/components/settlement-period-switch/settlement-period-switch.component';
import { BulkServiceInfoComponent } from 'app/order/components/bulk-service-info/bulk-service-info.component';
import { productAdviceTablet } from './steps';

const orderConfig: OrderProductConfig = {
    steps: [defaultLandingPage, insuredObjects, productAdviceTablet, billingAddress, paymentData, submitContract],
    taxInfo: 'Inkl. 19 % Versicherungssteuer',
    hasFlexPrice: true,
    showPriceFrom: true,
    showDiscountInput: true,
    naturalPersonsOnly: false,
};

const portalConfig: PortalProductConfig = {
    icon: 'tablet_reader',
    widgets: [...defaultContractWidgets, { component: TabletPlusInsuranceWidgetComponent }],
};

const presentation: ProductPresentation = {
    description: `
        <p><strong>Tabletversicherung Plus</strong></p>
        <p><strong class="c:blue-700">Der umfassende Versicherungsschutz für Schulen, Lehrer und Schüler oder die ganze Klasse für einzelne bzw. beliebig viele elektronische Geräte – inkl. Leihgeräten und Geräten von Lehrern mit hoher Nutzungsfrequenz. Inkl. Plus-Schutz bei Schäden durch Transport oder Verwendung zuhause!</strong></p>
        <p>Ob du ein einzelnes Gerät versichern möchtest, das in deinem Besitz ist, oder eines, das dir leihweise von der Schule überlassen wurde, ob du Schulträger oder Förderverein bist und gleich mehrere Geräte gut geschützt wissen willst – dann ist die speziell für digitale Schulen, Lehrer und Schüler konzipierte Tablet- und Notebook-Versicherung „Tablet PLUS“ genau das Richtige für dich.</p>
        <p>Zum günstigen Tarif umfassend gegen alle denkbaren Schäden <strong>im schulischen Umfeld, beim Transport, zuhause und bei sonstiger privater Nutzung</strong> geschützt: Neben dem klassischen Versicherungsschutz bei Brand, Blitzschlag, Explosion oder Wasserschäden deckt die Tablet- und Notebook-Versicherung „Tablet PLUS“ von astra nämlich auch wahrscheinlichere Schäden wie z.B. bei Verlust, Diebstahl und Herunterfallen in der Schule, beim Transport und sogar zuhause ab.</p>
        <p><strong class="c:blue-700">Schnell und einfach zum Vertrag - ab nur 1,99&nbsp;€ im Monat pro Notebook oder Tablet.<br>
        Der Versicherungsschutz „Tablet Plus“ für Schulen, Lehrer, Schüler und digitale Klassen.</strong></p>
        `,
    bulletPoints: [
        'Alle Risiken innerhalb der Schule, bei häuslichen Lerngruppen und privater Nutzung sind abgedeckt',
        'Deckung bei Totalschaden, Transportschäden, Verlust, Diebstahl, Herunterfallen u.v.m.',
        'Schutz auch für Lehrergeräte oder Leihgeräte mit hoher Nutzungsfrequenz',
        'Versicherungsschutz für einzelne oder beliebig viele Endgeräte',
    ],
    imageUrl: 'assets/img/product-headers/astra_versicherung_tabletversicherung_plus.jpg',
    imageAlt:
        'astra Versicherung „Tablet PLUS“: Die Tablet- und Notebook-Versicherung mit Schutz in der Schule UND zuhause.',
    imageHeadline:
        '<span class="h3">„Tablet PLUS“:</span><p class="m-0:b">Die Tablet- und Notebook-Versicherung mit Schutz in der Schule UND bei privater Nutzung.</p>',
    docIPIDUrl: 'astra_IPID_Tablet_Plus.pdf',
    docAVBUrl: 'astra_AVB_Tablet_Plus.pdf',
    docAddInfoUrl: 'astra_Kundeninfo_Tablet_Plus.pdf',
    docAnzeigepflichtUrl: 'Obliegenheiten_und_vorvertragliche_Anzeigepflicht.pdf',
};

const insuredObjectsForm: InsuredObjectFormConfig = {
    headline: 'Angaben zu versicherten Geräten',
    description: `<p>Bitte mache einige Angaben, zu dem/den Gerät/en, die du versichern möchtest.
        Du kannst jederzeit ein weiteres Gerät hinzufügen, in dem du auf die Schaltfläche "Weiteres Gerät hinzufügen" klickst.
        Der Beitrag deiner Versicherung bemisst sich nach Anzahl und Neupreis der versicherten Geräte.</p>`,
    objectDescriptionSingular: 'Gerät',
    objectDescriptionPlural: 'Geräte',
    max: 999,
    addButtonLabel: 'Weiteres Gerät hinzufügen',
    note: '<strong>Hinweis:</strong> Du kannst die Seriennummer nach Abschluss im Kundenportal ergänzen. \
    Der Versicherungsschutz besteht erst nach Ergänzung.',
    additionalComponents: [BulkServiceInfoComponent, SettlementPeriodSwitchComponent],
    controls: [
        {
            name: 'description',
            label: 'Hersteller',
            type: 'text',
            required: true,
            validators: CommonValidators.something,
            errorMsg: { required: MANUFACTUREUR_REQUIRED, minlength: MANUFACTUREUR_TOO_SHORT },
        },
        {
            name: 'value',
            label: 'Kaufpreis',
            type: 'value',
            required: true,
            validators: CommonValidators.priceBetween(1, 1500).concat(Validators.required),
            errorMsg: {
                required: PRICE_UP_TO_1500,
                noValidPrice: PRICE_UP_TO_1500,
                priceTooLow: PRICE_UP_TO_1500,
                priceToHigh: PRICE_UP_TO_1500,
            },
        },
        {
            name: 'detail',
            label: 'Seriennummer',
            type: 'text',
        },
    ],
};

export const tabletVersicherungPlus: ProductWithVariants = getProductWithVariants({
    slug: 'tablet-versicherung-plus',
    title: 'Tabletversicherung Plus',
    fullTitle: 'Tablet- und Notebook-Versicherung "Tablet PLUS"',
    insuranceTypeName: 'Tablet Plus (M)',
    insuranceTypeId: 22,
    orderConfig,
    portalConfig,
    presentation,
    insuredObjectsForm,
    tags: ['showCustomerServiceNumber', 'hasMonthlySettlementPeriod'],
    variants: [
        {
            overrides: {
                slug: 'tablet-versicherung-plus-j',
                insuranceTypeName: 'Tablet Plus (J)',
                insuranceTypeId: 24,
                tags: ['showCustomerServiceNumber'],
            },
            condition: (state: OrderState) => !state.tags.get('hasMonthlySettlementPeriod'),
        },
    ],
    onInit: (state: Pick<OrderState, 'tags'>, product: Product) => ({
        ...state,
        tags: new Map([...state.tags, ...getTagsFromProduct(product)]),
    }),
});
